import {
	Button,
	FormItem,
	InputRow,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import { AlertManager, useAlerts } from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import { isNotEmpty, isNotNullNorUndefined } from "client/Validation/rules";
import { AppType } from "client/Api/Apps/common";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { useSaveApp, EditAppSavedResult } from "../useSaveApp";
import {
	getZermeloConnections,
	ZermeloConnectionSelectOptionModel,
	ZermeloConnectionSelectOptionsModel
} from "client/Api/Apps/EditApp/appParameters";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { ZermeloConnectionDialog } from "client/Features/Settings/Connections/Zermelo/ZermeloConnectionDialog";
import { ZermeloConnectionListItemModel } from "client/Api/ZermeloConfiguration/zermeloConfiguration";
import {
	SaveZermeloAppModel,
	EditZermeloAppViewModel,
	saveZermeloApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveZermeloAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

export type EditZermeloAppProps = {
	initialSettings: EditAppSettings<EditZermeloAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditZermeloApp = forwardRef(
	(
		props: EditZermeloAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [zermeloConnectionSelectOptionsModel, setZermeloConnectionSelectOptionsModel] =
			useState<ZermeloConnectionSelectOptionsModel>({
				canCreateNew: false,
				options: []
			});

		const [formData, setFormData] = useState<SaveZermeloAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState<boolean>(false);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return zermeloConnectionSelectOptionsModel.options
					.map(
						o => ({
							value: o.id,
							text: o.name
						})
					);
			},
			[zermeloConnectionSelectOptionsModel.options]
		);

		const alertManager: AlertManager = useAlerts();

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveZermeloAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const addConnectionHandler = useCallback(
			(item: ZermeloConnectionListItemModel): void => {
				setZermeloConnectionSelectOptionsModel(
					(existingOptionsModel: ZermeloConnectionSelectOptionsModel) => {
						const existingItemIndex: number = existingOptionsModel.options
							.findIndex(i => i.id === item.id);

						const options: ZermeloConnectionSelectOptionModel[] = [
							...existingOptionsModel.options
						];

						if (existingItemIndex === -1) {
							options.push(item);
						} else {
							options.splice(existingItemIndex, 1, item);
						}

						return {
							...existingOptionsModel,
							options
						};
					}
				);

				formHandler.for("connectionId")
					.attachToSelect()
					.onChange(item.id);

			},
			[formHandler]
		);

		useEffect(
			() => {
				async function loadZermeloConnections(signal: AbortSignal): Promise<void> {
					try {
						const result: ZermeloConnectionSelectOptionsModel =
							await getZermeloConnections(signal);

						setZermeloConnectionSelectOptionsModel(result);
					} catch (e) {
						if (isCancelledError(e)) {
							return;
						}

						alertManager.showError(localize("_GeneralErrorMessage"));
					}
				}

				const cancellation = new AbortController();
				loadZermeloConnections(cancellation.signal);

				return () => cancellation.abort();
			},
			[alertManager]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveZermeloAppModel>(
			formHandler,
			saveZermeloApp,
			AppType.Zermelo,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<>
				<EditAppBase
					formHandler={formHandler}
					hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
					initialAppSettings={app}
					usersAndUserGroupsData={usersAndUserGroupsData}
					initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
				>
					<FormItem
						label={localize("_Connection")}
					>
						<InputRow>
							<InputRow.Stretch>
								<Select
									{...formHandler.for("connectionId")
										.attachToSelect()}
									options={connectionOptions}
									placeholder={localize("_SelectConnection")}
									menuTitle={localize("_Connection")}
								/>
							</InputRow.Stretch>
							{zermeloConnectionSelectOptionsModel.canCreateNew && (
								<Button
									text={localize("_Add")}
									onClick={() => setIsConnectionDialogOpen(true)}
									kind="secondary"
									trackingId="add-new-connection-button"
								/>
							)}
						</InputRow>
						<ValidationError
							{...formHandler.for("connectionId")
								.attachToValidationError()}
						/>
					</FormItem>
					<AppDestinationWindowSelectFormItem
						attachable={formHandler.for("destinationWindow")}
					/>
				</EditAppBase>
				{isConnectionDialogOpen && (
					<ZermeloConnectionDialog
						onAfterSave={addConnectionHandler}
						onClose={() => setIsConnectionDialogOpen(false)}
					/>
				)}
			</>
		);
	}
);

EditZermeloApp.displayName = "EditZermeloApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditZermeloAppViewModel>
): SaveZermeloAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}